/**
 * Which app pages the public demo can actually show.
 *
 * `/demo` renders the real authenticated shell, so the whole sidebar is on
 * screen - roughly forty items. Only the pages listed here have fixtures behind
 * them. Everything else used to be a live link into a `<ProtectedRoute>`, which
 * bounced an anonymous visitor to `/login?returnUrl=...`: a sign-in wall in
 * front of someone who, by definition, has no account. That is the report this
 * module exists to fix.
 *
 * Kept as its own module rather than a flag on `AppNavItem`, because
 * `navigationConfig` is shared with the command palette, the persona cards and
 * the pinned sidebar items, and "can the demo render this" is not part of that
 * shared vocabulary.
 *
 * `coverage.test.ts` parses the `/demo` child routes out of `App.tsx` and
 * asserts this set matches them exactly, so adding a route without adding
 * coverage (or the reverse) fails CI rather than shipping a half-wired page.
 */

/**
 * App paths reachable inside the demo. Each has a matching `/demo/*` child
 * route, and every endpoint those pages call is answered by `lib/demo/router`.
 *
 * Chosen by ICP value against fixture cost. The primary ICP (VP/Director) gets
 * the Executive Summary and the Velocity Graph; the secondary (EM/Team Lead)
 * gets the Review Network and the Developer League; the tertiary
 * (Staff/Principal) gets File Hotspots. Between them they need seven endpoints.
 *
 * The individual developer detail page was deliberately excluded at first - it
 * alone needs eleven endpoints, and its framing ("here is one person's numbers")
 * is the surveillance reading we avoid putting in front of a prospect before
 * they have the team-level framing. It was added later once the drilldown from
 * the Review Network ("Priya gives 32% of the reviews" -> her profile) proved
 * to be the moment the product clicks, and only with every ranking, percentile
 * and vs-team comparison omitted from the generator, so the page answers "what
 * is this person's work like" rather than "how does this person rank".
 */
export const DEMO_COVERED_PATHS: ReadonlySet<string> = new Set([
  '/dashboard',
  '/executive',
  '/velocity',
  '/review-network',
  '/developers',
  '/developers/:id',
  '/developer-league',
  '/file-hotspots',
]);

/**
 * True if `path` (query string tolerated) can be shown inside the demo.
 *
 * Handles the one parameterised route as well: `/developers/dev-priya` is
 * covered because `/developers/:id` is. Without that, every developer link on
 * the review network and the league table would render with a lock on it.
 */
export function isDemoCovered(path: string): boolean {
  const base = basePath(path);
  if (DEMO_COVERED_PATHS.has(base)) return true;
  return [...DEMO_COVERED_PATHS].some((covered) => matchesParameterised(covered, base));
}

/** `/developers/:id` matches `/developers/dev-priya`. */
function matchesParameterised(pattern: string, path: string): boolean {
  if (!pattern.includes('/:')) return false;
  const patternParts = pattern.split('/');
  const pathParts = path.split('/');
  if (patternParts.length !== pathParts.length) return false;
  return patternParts.every((part, i) => part.startsWith(':') || part === pathParts[i]);
}

/**
 * Map an app path to its demo equivalent.
 *
 * `/dashboard` is the demo's index route, so it maps to `/demo` rather than
 * `/demo/dashboard` - there is no such child route, and linking to one would
 * hit the catch-all.
 */
export function toDemoPath(path: string): string {
  const base = basePath(path);
  return base === '/dashboard' ? '/demo' : `/demo${base}`;
}

function basePath(path: string): string {
  return path.split('?')[0].split('#')[0];
}
